import { AnalyticsPeriodService } from '@/utils/services/analytics/AnalyticsPeriodService';
import { TimeService } from '@/utils/services/TimeService';

import { get } from '@/utils/client';

import type {
    DatepickerComparisonPeriodsAvailableType,
    DatepickerPeriodsAvailableType
} from '@/lib/periods';

/**
 * Configuration for analytics parameters (e.g., media type, id_media)
 */
export type AnalyticsParameterConfig = {
    type: string;
    id_media?: string | null;
};

/**
 * Parameters for Analytics V2 API requests
 */
export type AnalyticsParams = {
    start?: string;
    end?: string;
    fields?: string[] | string[][];
    period?: DatepickerPeriodsAvailableType;
    comparison_period?: DatepickerComparisonPeriodsAvailableType;
    album_id?: string;
    disable_previous?: boolean;
    parameters?: Record<string, AnalyticsParameterConfig>;
    [key: string]: unknown;
};

/**
 * Record of analytics fields (more permissive than AnalyticsRecord)
 */
export type AnalyticsRecord = Record<string, AnalyticsFieldData | null>;

/**
 * Full analytics response structure
 */
export type AnalyticsData = {
    analytics?: AnalyticsRecord;
    dates?: unknown;
};

/**
 * Individual field data in analytics response
 */
export type AnalyticsFieldData = {
    value?: unknown;
    graphData?: unknown[];
    previous?: number | null;
    evolution?: number | null;
    data_complete?: boolean;
    [key: string]: unknown;
};

/**
 * Request parameters for media-specific analytics
 */
export type MediaAnalyticsRequestParams = {
    period: string;
    start: string;
    end: string;
    fields: string[];
    disable_previous: boolean;
};

type BuildMediaAnalyticsParamsOptions = {
    mediaId: number | string;
    requiredFields: string[];
    startDate?: string | Date;
    endDate?: string | Date;
};

type FormatParamsForV2Options = {
    params: AnalyticsParams;
    keepComparisonPeriod?: boolean;
};

type FetchAndMergeComparisonOptions = {
    url: string;
    apiParams: AnalyticsParams;
    requestParams: AnalyticsParams;
    currentData: AnalyticsRecord;
};

class AnalyticsDataService {
    /**
     * Format and clean params for Analytics V2 API.
     * Handles date formatting and conditional param removal based on album_id presence.
     */
    formatParamsForV2 = ({
        params,
        keepComparisonPeriod = false
    }: FormatParamsForV2Options): AnalyticsParams => {
        const formattedParams = { ...params } as AnalyticsParams;

        if (!keepComparisonPeriod) {
            delete formattedParams.comparison_period;
            delete formattedParams.period;
        }

        if (formattedParams.start) {
            formattedParams.start =
                TimeService.formatDateForAnalyticsV2(formattedParams.start) ??
                formattedParams.start;
        }

        if (formattedParams.end) {
            formattedParams.end =
                TimeService.formatDateForAnalyticsV2(formattedParams.end, true) ??
                formattedParams.end;
        }

        if (formattedParams.album_id) {
            delete formattedParams.period;
            delete formattedParams.comparison_period;
            delete formattedParams.start;
            delete formattedParams.end;
        } else {
            delete formattedParams.album_id;
        }

        return formattedParams;
    };

    /**
     * Fetch comparison data and merge with current data.
     * Returns current data as-is if no comparison is requested or disable_previous is true.
     */
    fetchAndMergeComparison = async ({
        url,
        apiParams,
        requestParams,
        currentData
    }: FetchAndMergeComparisonOptions): Promise<AnalyticsRecord> => {
        // If no comparison requested or disable_previous is true, return current data as-is
        if (!requestParams.comparison_period || requestParams.disable_previous) {
            return currentData;
        }

        // Need valid start/end dates for comparison calculation
        if (!requestParams.start || !requestParams.end) {
            return currentData;
        }

        try {
            const { startComparison, endComparison } =
                AnalyticsPeriodService.calculateComparisonPeriod({
                    start: requestParams.start,
                    end: requestParams.end,
                    compareTo: requestParams.comparison_period,
                    period: requestParams.period,
                    timezone: 'UTC'
                });

            const comparisonParams = {
                ...apiParams,
                start: startComparison,
                end: endComparison
            };

            let comparisonData;
            if (requestParams?.fields?.[0] && Array.isArray(requestParams?.fields?.[0])) {
                const comparisonResponses = await Promise.all(
                    (requestParams.fields as string[][]).map((fields) =>
                        get(url, { ...comparisonParams, fields }).then((res) => res.data)
                    )
                );
                comparisonData = comparisonResponses.reduce(
                    (result, current) => ({ ...result, ...current }),
                    {}
                );
            } else {
                const comparisonResponse = await get(url, comparisonParams);
                comparisonData = comparisonResponse.data;
            }

            const normalizedComparisonData = this.normalizeV2AnalyticsData(comparisonData);

            return this.mergeAnalyticsWithComparison(currentData, normalizedComparisonData);
        } catch (error) {
            console.warn(
                '[AnalyticsDataService]: Failed to fetch comparison data, returning current data only',
                error
            );
            return currentData;
        }
    };

    /**
     * Fetcher for Analytics V2 API.
     * Handles both single and multiple field arrays, normalizes data, and merges comparison if needed.
     */
    fetchAnalyticsV2 = async (
        url: string,
        requestParams: AnalyticsParams
    ): Promise<AnalyticsData> => {
        const apiParams = this.formatParamsForV2({ params: requestParams });

        let currentData;
        if (requestParams?.fields?.[0] && Array.isArray(requestParams?.fields?.[0])) {
            const responses = await Promise.all(
                (requestParams.fields as string[][]).map((fields) =>
                    get(url, { ...apiParams, fields }).then((res) => res.data)
                )
            );
            currentData = responses.reduce((result, current) => ({ ...result, ...current }), {});
        } else {
            const response = await get(url, apiParams);
            currentData = response.data;
        }

        const normalizedCurrentData = this.normalizeV2AnalyticsData(currentData);

        const finalData = await this.fetchAndMergeComparison({
            url,
            apiParams,
            requestParams,
            currentData: normalizedCurrentData ?? {}
        });

        return { analytics: finalData };
    };

    /**
     * Normalizes Industry Benchmark analytics response by adding graphData from value object.
     * Specifically for pie charts where `value` is an object (distribution data).
     * Creates `graphData: [value]` to maintain compatibility with pie chart components.
     *
     * Example:
     * Input:  { engagement_rate: { value: { avg: 3.5, median: 3.2 } } }
     * Output: { engagement_rate: { value: { avg: 3.5, median: 3.2 }, graphData: [{ avg: 3.5, median: 3.2 }] } }
     */
    normalizeIndustryBenchmarkData = (
        analytics: AnalyticsRecord | null
    ): AnalyticsRecord | null => {
        if (!analytics) {
            return analytics;
        }

        const transformed: AnalyticsRecord = {};

        Object.keys(analytics).forEach((fieldKey) => {
            const fieldData = analytics[fieldKey];
            if (!fieldData) {
                transformed[fieldKey] = fieldData;
                return;
            }

            const isValueObject =
                typeof fieldData.value === 'object' &&
                fieldData.value !== null &&
                !Array.isArray(fieldData.value);

            transformed[fieldKey] = isValueObject
                ? { ...fieldData, graphData: [fieldData.value] }
                : fieldData;
        });

        return transformed;
    };

    /**
     * Normalizes V2 analytics response for time series and distribution data.
     * Handles multiple cases:
     * 1. When `value` is an array (time series) → converts to `graphData` format
     * 2. When `value` already contains `graphData` (e.g., best_time_to_post) → flattens the structure
     * 3. When `value` is a distribution object (e.g., average_impressions_per_post_type) → transforms to graphData array
     *
     * Examples:
     * Input:  { followers_count: { value: [100, 200, 300] } }
     * Output: { followers_count: { graphData: [100, 200, 300] } }
     *
     * Input:  { best_time_to_post: { value: { graphData: [...], min: 0, max: 100 } } }
     * Output: { best_time_to_post: { graphData: [...], min: 0, max: 100 } }
     *
     * Input:  { average_impressions_per_post_type: { value: { status: 70193, photo: 54598 } } }
     * Output: { average_impressions_per_post_type: { graphData: [{ type: 'status', value: 70193 }, ...], max: 70193 } }
     */
    normalizeV2AnalyticsData = (data: AnalyticsRecord | null): AnalyticsRecord | null => {
        if (!data) return data;

        const normalizedAnalytics: AnalyticsRecord = {};

        Object.keys(data).forEach((key) => {
            const field = data[key];
            if (!field) {
                normalizedAnalytics[key] = field;
                return;
            }

            // Case 1: Convert value arrays to graphData to match V1 logic
            if (Array.isArray(field.value)) {
                normalizedAnalytics[key] = {
                    graphData: field.value
                };
            } else if (
                typeof field.value === 'object' &&
                field.value !== null &&
                !Array.isArray(field.value)
            ) {
                const valueObj = field.value as Record<string, unknown>;
                // Case 2: value already contains graphData (e.g., best_time_to_post)
                if ('graphData' in valueObj) {
                    normalizedAnalytics[key] = { ...valueObj } as AnalyticsFieldData;
                } else {
                    // Case 3: value is a distribution object (e.g., average_impressions_per_post_type)
                    const graphData = Object.entries(valueObj).map(([type, value]) => ({
                        type,
                        value
                    }));
                    const valuesArray = Object.values(valueObj).map((v) =>
                        typeof v === 'number' ? v : 0
                    );
                    const maxValue = valuesArray.length > 0 ? Math.max(...valuesArray) : 0;
                    normalizedAnalytics[key] = {
                        graphData,
                        max: maxValue
                    };
                }
            } else {
                normalizedAnalytics[key] = field;
            }
        });

        return normalizedAnalytics;
    };

    /**
     * Merge current analytics data with comparison period data
     * Adds 'previous' and 'evolution' fields to each KPI
     * @param currentData - Analytics data for the current period
     * @param comparisonData - Analytics data for the comparison period
     * @returns Merged analytics data with comparison metrics
     */
    mergeAnalyticsWithComparison = (
        currentData: AnalyticsRecord,
        comparisonData: AnalyticsRecord | null | undefined
    ): AnalyticsRecord => {
        if (!currentData) return currentData;

        const mergedData: AnalyticsRecord = { ...currentData };

        Object.keys(currentData).forEach((kpiKey) => {
            const currentKPI = currentData[kpiKey];
            const comparisonKPI = comparisonData?.[kpiKey];

            // Skip if this is not a KPI with a value (might be graphData only)
            if (typeof currentKPI?.value !== 'number') {
                return;
            }

            // Add comparison data if available, otherwise set to null for consistent shape
            if (comparisonKPI && typeof comparisonKPI.value === 'number') {
                mergedData[kpiKey] = {
                    ...currentKPI,
                    previous: comparisonKPI.value,
                    evolution: AnalyticsPeriodService.calculateEvolution(
                        currentKPI.value,
                        comparisonKPI.value
                    )
                };
            } else {
                // No comparison data available, set to null (not undefined) for consistent shape
                mergedData[kpiKey] = {
                    ...currentKPI,
                    previous: null,
                    evolution: null
                };
            }
        });

        return mergedData;
    };

    /**
     * Builds request parameters for fetching media-specific analytics.
     * Formats dates as ISO 8601 for Analytics V2 API (required format: Y-m-d\TH:i:s.v\Z)
     *
     * Note: Media ID is passed in the URL path, not in the request body.
     * Uses endpoint: /v2/social-profile/{SPID}/medias/{mediaID}/analytics
     *
     * @param options.mediaId - The media ID (used for validation, actual ID is in URL)
     * @param options.requiredFields - Array of field names to request
     * @param options.startDate - Optional start date (defaults to today)
     * @param options.endDate - Optional end date (defaults to today)
     * @returns Request params object or null if invalid inputs
     *
     * @example
     * Input:  { mediaId: '123', requiredFields: ['likes', 'replies'], startDate: '2026-01-12', endDate: '2026-01-12' }
     * Output: { period: 'custom', start: '2026-01-12T00:00:00.000Z', end: '2026-01-12T23:59:59.999Z', fields: ['likes', 'replies'], disable_previous: true }
     */
    buildMediaAnalyticsParams = ({
        mediaId,
        requiredFields,
        startDate: inputStartDate,
        endDate: inputEndDate
    }: BuildMediaAnalyticsParamsOptions): MediaAnalyticsRequestParams | null => {
        if (!mediaId || !requiredFields.length) {
            return null;
        }

        // Use provided dates or fall back to today
        const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
        const startDateStr =
            inputStartDate instanceof Date
                ? inputStartDate.toISOString().split('T')[0]
                : inputStartDate || today;
        const endDateStr =
            inputEndDate instanceof Date
                ? inputEndDate.toISOString().split('T')[0]
                : inputEndDate || today;

        // Format dates as ISO 8601 for Analytics V2 API
        const startDate = TimeService.formatDateForAnalyticsV2(startDateStr, false);
        const endDate = TimeService.formatDateForAnalyticsV2(endDateStr, true);

        if (!startDate || !endDate) {
            return null;
        }

        return {
            period: 'custom',
            start: startDate,
            end: endDate,
            fields: requiredFields,
            disable_previous: true
        };
    };
}

const analyticsDataService = new AnalyticsDataService();

export { analyticsDataService as AnalyticsDataService };
